<template>
    <div
        class="apprentice-simple-card user-profile-widget"
        :class="latestCards.length === 0 && 'hide-timeline-bar'">
        <div class="component-title">
            <h5 class="title">{{ $t('profile.timeline') }}</h5>
            <div class="actions">
                <b-button
                    class="btn-share btn-share-timeline"
                    v-b-tooltip.hover
                    v-if="!teacherAccess"
                    variant="primary-icon-only"
                    :title="$t('profile.share_timeline')"
                    @click="showShareModal">
                </b-button>

                <b-button
                    class="btn-download btn-download-pdf"
                    variant="primary-icon-only"
                    :title="$t('profile.download_pdf_timeline')"
                    @click="downloadPDF"></b-button>

                <b-button
                    class="btn-add btn-add-time-lime"
                    v-if="!teacherAccess"
                    v-b-tooltip.hover
                    variant="primary-icon-only"
                    :title="$t('profile.add_timeline_item')"
                    @click="showNewProfileCardModal">
                </b-button>

                <!-- TODO: Verify translation "Tijdlijn openen" -->
                <b-button
                    class="btn-show btn-open-timeline"
                    v-if="latestCards.length === 0"
                    v-b-tooltip.hover
                    variant="primary-icon-only"
                    @click="openTimeline"
                    :title="$t('profile.timeline')">
                </b-button>
            </div>
        </div>

        <design-2021-profile-add-card-component
            ref="newProfileCard"
            :categories-in="categoriesIn"
            :seasons-in="seasonsIn"
            @add-card="addCard">
        </design-2021-profile-add-card-component>

        <design-2021-profile-update-card-component
            ref="profile-update-card-modal"
            :categories-in="categoriesIn"
            :seasons-in="seasonsIn"
            @update-card="updateCard">
        </design-2021-profile-update-card-component>

        <design-2021-profile-share-modal-component
            :school-years="schoolYears"
            :categories-in="categoriesIn"
            :seasons-in="seasonsIn"
            :cards-in="cards">
        </design-2021-profile-share-modal-component>

        <div
            class="not-found with-header empty-timeline"
            v-if="latestCards.length === 0">
            <div
                class="btn-add-circle icon"
                v-if="!teacherAccess"
                v-b-modal="'profile-add-card-modal'"
                v-b-tooltip.hover
                :title="$t('profile.to_timeline')"></div>
            <div
                class="alert-notice"
                v-if="!teacherAccess">
                {{ $t('profile.you_have_not_created_a_timeline_item_yet') }}
                <p>{{ $t('profile.use_the_bottom_above_to_create_a_timeline_item') }}</p>
            </div>
            <div
                class="alert-notice"
                v-else>
                {{ $t('profile.no_timeline_items_created') }}
            </div>
        </div>

        <div
            class="timeline-profile"
            v-else>
            <div
                class="timeline-profile-wrapper timeline-profile-inverted timeline-profile-wrapper-warning"
                v-for="card in latestCards">
                <div class="timeline-profile-date d-sm-block d-none">{{ card.date | date_written }}</div>
                <div
                    class="timeline-profile-badge"
                    :style="cssVars(card)"></div>
                <div
                    class="timeline-profile-panel"
                    v-bind:style="{ borderTopColor: card.category.color }">
                    <b-media
                        :class="
                            card.image_files?.length !== 0 &&
                            card.video_files?.length !== 0 &&
                            card.video_links?.length !== 0
                                ? 'no-media'
                                : ''
                        ">
                        <template
                            v-if="
                                card.image_files?.length !== 0 ||
                                card.video_files?.length !== 0 ||
                                card.video_links?.length !== 0
                            "
                            #aside>
                            <h5 class="d-sm-none d-block timeline-profile-date">{{ card.shortdate }}</h5>
                            <design-2021-profile-timeline-media-widget-component
                                :image-files="card.image_files"
                                :video-files="card.video_files"
                                :video-links="card.video_links">
                            </design-2021-profile-timeline-media-widget-component>
                        </template>

                        <template
                            v-else
                            #aside>
                            <h5 class="d-sm-none d-block timeline-profile-date">{{ card.shortdate }}</h5>
                            <b-img src="/images/no-media.jpg"></b-img>
                        </template>

                        <h5 class="title">{{ card.title }}</h5>
                        <div
                            class="timeline-profile-category"
                            v-if="card.category">
                            {{ card.category.title }}
                        </div>
                        <design-2021-apprentice-display-html-component
                            class="timeline-content"
                            v-trim="20"
                            :html-content="card.text"></design-2021-apprentice-display-html-component>
                        <div class="btn-show-more btn-timeline-profile-more">{{ $t('profile.read_more') }}</div>
                    </b-media>
                    <a
                        class="clickable"
                        href="#"
                        @click.prevent.stop="viewProfileCard(card.id)"></a>
                </div>
            </div>
            <div class="show-main-timeline">
                <b-button
                    class="btn-show-all btn-display-profile-page"
                    variant="link-type"
                    @click="openTimeline">
                    {{ $t('common.more') }}
                </b-button>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'design-2021-profile-timeline-widget-component',
    props: {
        cardsIn: Array,
        teacherAccess: Boolean,
        userCredentialsIn: Object,
        categoriesIn: Array,
        seasonsIn: Array,
    },
    data() {
        return {
            profileUrl: this.teacherAccess
                ? this.routes.profile() + '/' + this.userCredentialsIn.id
                : this.routes.profile(),
            cards: this.getAllCards(),
            categories: this.getAllCategories(),
            selectedSchoolYears: this.seasonsIn,
        };
    },

    computed: {
        latestCards() {
            if (this.cards.length) {
                return this.cards.slice(0, 3).map((card) => {
                    if (card.category_id === 0) {
                        return {
                            ...card,
                            category: {
                                color: '#07A1FF',
                                id: 0,
                                title: this.$t('profile.not_connected'),
                            },
                        };
                    } else {
                        return card;
                    }
                });
            }
            return [];
        },

        schoolYears() {
            let years = [];

            for (const card of this.cards) {
                if (!years.some((year) => year.name === card.school_year)) {
                    years.push({
                        name: card.school_year,
                        selected: this.selectedSchoolYears.includes(card.school_year),
                    });
                }
            }

            return years;
        },
    },

    methods: {
        cssVars(timeline) {
            if (timeline.category) {
                return {
                    '--border-color': timeline.category.color,
                };
            }
        },
        openTimeline() {
            window.location.href = this.profileUrl;
        },

        addCard(data) {
            this.alert.saving();
            this.axios.post(this.routes.profileCard(), data).then(
                (response) => {
                    this.cards.push(response.data);
                    this.cards = this.cards.sort((a, b) => {
                        return new Date(b.date) - new Date(a.date);
                    });

                    this.alert.success();
                },
                (error) => {
                    this.alert.ajaxError(error);
                },
            );
        },

        updateCard(data) {
            this.alert.saving();
            this.axios.post(this.routes.profileCardUpdate(data.id), data).then(
                (response) => {
                    this.cards = this.cards.map((card) => {
                        return card.id !== data.id ? card : response.data;
                    });

                    this.cards = this.cards.sort((a, b) => {
                        return new Date(b.date) - new Date(a.date);
                    });

                    this.alert.success();
                },
                (error) => {
                    this.alert.ajaxError(error);
                },
            );
        },

        showProfilePage() {
            window.location.href = this.profileUrl;
        },

        getAllCards() {
            if (this.cardsIn.length >= 1) {
                return this.cardsIn.map((card) => {
                    if (!this.categoriesIn.some((category) => category.id === card.category_id)) {
                        card.category_id = 0;
                        return card;
                    } else {
                        return card;
                    }
                });
            }

            return [];
        },

        getAllCategories() {
            const categories = this.categoriesIn.map((category) => {
                return {
                    value: category.id,
                    text: category.title,
                };
            });

            categoriesLoop: for (const categoryIn of this.categoriesIn) {
                for (const cat of categories) {
                    if (categoryIn.category_id !== cat.value) {
                        categories.push({
                            value: 0,
                            text: this.$t('profile.not_connected'),
                        });
                        break categoriesLoop;
                    }
                }
            }

            return categories;
        },

        viewProfileCard(cardId) {
            window.location.href = this.teacherAccess
                ? this.routes.profileTeacherAccessViewCard(this.userCredentialsIn.id, cardId)
                : this.routes.profileViewCard(cardId);
        },

        showNewProfileCardModal() {
            this.$refs.newProfileCard.show(null);
        },

        showShareModal() {
            this.$bvModal.show('profile-share-modal');
        },

        downloadPDF() {
            window.open(this.routes.profileDownloadPDF(this.userCredentialsIn.id), '_blank');
        },
    },
};
</script>

<style scoped lang="scss">
.component-title ::v-deep {
    .dropdown-toggle {
        font-size: $base-font-size;
    }

    .actions {
        display: flex;
        align-items: center;
        justify-content: flex-end;
        margin-left: auto;

        .btn {
            &:not(:last-child) {
                margin-right: map-get($grid-gutter-spacing, 'fifteen');
            }

            &:before {
                margin-right: 0;
            }
        }
    }
}

.user-profile-widget {
    padding-bottom: map-get($grid-gutter-spacing, 'ten') * 3.7;
}

.component-title .title {
    align-self: center;
}

.timeline-profile {
    .timeline-profile-wrapper {
        &:not(:last-child) {
            margin-bottom: map-get($grid-gutter-spacing, 'twenty-one');
        }
    }

    &.hide-timeline-bar:before {
        display: none;
    }
}

.timeline-profile-panel {
    position: relative;
    border: 1px solid $border-color;
    border-radius: $border-radius-content;
    border-top-left-radius: 0 !important;
    border-top-right-radius: 0 !important;
    border-top: 3px solid;
    border-top-color: var(--border-color);
    padding: map-get($grid-gutter-spacing, 'fifteen');
    background: $secondary-background-color;
    top: 16px;
    box-shadow: none;

    .media {
        flex-direction: column;

        > div {
            &:first-of-type {
                flex-direction: column;
                width: 100%;
                margin-right: 0 !important;
                margin-bottom: map-get($grid-gutter-spacing, 'fifteen');
            }
        }

        .media-body {
            display: flex;
            flex-direction: column;
            width: 100%;
            align-self: stretch;
        }

        .timeline-profile-date {
            align-self: flex-start;
        }

        .title {
            margin-bottom: 0;
        }

        .timeline-profile-category {
            color: $text-muted-color;
            font-weight: $font-weight-medium;
            margin-bottom: map-get($grid-gutter-spacing, 'eight');
        }

        .btn-timeline-profile-more {
            font-weight: $font-weight-medium;
            align-self: flex-end;
        }
    }

    &:before {
        position: absolute;
        top: 0;
        height: 2px;
        content: '';
        left: 0;
        right: 0;
    }

    &:after {
        display: none;
    }

    &:hover {
        background-color: darken($secondary-background-color, 5%);

        &:after,
        .clickable:after {
            border-top-color: darken($secondary-background-color, 5%);
        }
    }
}

.show-main-timeline {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    margin-top: map-get($grid-gutter-spacing, 'forty-two');

    .btn {
        font-weight: $font-weight-bold;
    }
}

@media (min-width: map-get($grid-breakpoints, 'sm')) {
    .timeline-profile {
        list-style: none;
        position: relative;

        &:before {
            top: 0;
            bottom: 0;
            position: absolute;
            content: ' ';
            width: 3px;
            background-color: $border-color;
            left: 61px;
            margin-left: -1.5px;
        }
    }

    .timeline-profile-wrapper {
        position: relative;
    }

    .timeline-profile-date {
        position: relative;
        max-width: 52px;
        font-size: 0.8125rem;
        line-height: 1.3;
        color: $text-muted-color;
        margin-top: map-get($grid-gutter-spacing, 'eight');
        float: left;
    }

    .timeline-profile-badge {
        width: 22px;
        height: 22px;
        position: absolute;
        top: 5px;
        left: 51px;
        z-index: 10;
        border-top-right-radius: 50%;
        border-top-left-radius: 50%;
        border-bottom-right-radius: 50%;
        border-bottom-left-radius: 50%;
        border: 4px solid var(--border-color);
        background-color: $white;

        i {
            color: $white;
        }

        &:before {
            content: '';
            height: 30px;
            width: 30px;
            background: transparent;
            display: block;
            position: absolute;
            left: -8px;
            top: -8px;
            z-index: -1;
            border: 5px solid $white;
            border-radius: 25px;
        }
    }

    .timeline-profile-panel {
        margin-left: 100px;

        .media {
            flex-direction: row;

            > div:first-of-type {
                width: 150px;
                object-fit: cover;
                margin-right: map-get($grid-gutter-spacing, 'twenty-one') !important;
            }
        }

        .clickable:after,
        &:after {
            content: '';
            border-top: 1em solid $secondary-background-color;
            border-left: 1em solid transparent;
            left: -13px;
            position: absolute;
            display: block;
            top: 0;
        }
    }
}
</style>
